import { useState } from 'react';
import { clsx } from 'clsx';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { Download, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import type ExportModel from '@/models/entities/ExportModel';
import { EXPORT_STATUS } from '@/models/references/references';
import { useExportStore } from '@/stores/useExportStore';
import { useReferenceOptions } from '@/stores/useReferenceOptions';
import { createExport, describeFilters, downloadExport, type FilterNames } from './exports';

/** Couleur par statut : en attente gris, en cours orange, terminé vert, échoué rouge */
const TONES: Record<string, string> = {
  [EXPORT_STATUS.PENDING]: 'bg-slate-100 text-slate-700 border-slate-200',
  [EXPORT_STATUS.PROCESSING]: 'bg-amber-100 text-amber-700 border-amber-200',
  [EXPORT_STATUS.COMPLETED]: 'bg-green-100 text-green-700 border-green-200',
  [EXPORT_STATUS.FAILED]: 'bg-red-100 text-red-700 border-red-200',
};

/** Statut d'un export, libellé de l'API (« En cours »…) */
export function ExportStatusBadge({ model }: { model: ExportModel }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-xs font-normal px-2 py-0.5 rounded-full border whitespace-nowrap',
        TONES[model.exportStatusReference] ?? TONES[EXPORT_STATUS.PENDING],
      )}
      data-testid="export-status"
    >
      {model.isRunning && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
      {model.exportStatusLabel}
    </span>
  );
}

/** Résumé des filtres d'un export (« Septembre 2026 · DEV ») */
export function ExportFiltersLabel({ model, names }: { model: ExportModel; names: FilterNames | null }) {
  const timeSheetTypes = useReferenceOptions('timeSheetTypes');
  return <>{describeFilters(model.filters, names, timeSheetTypes)}</>;
}

/**
 * Action d'un export : le télécharger une fois terminé, le relancer s'il a échoué (nouvel export, mêmes type et
 * filtres) ; rien pendant la génération
 */
export function ExportAction({ model }: { model: ExportModel }) {
  const [busy, setBusy] = useState(false);
  const refresh = useExportStore((state) => state.refresh);

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(getApiErrorMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (model.isCompleted) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() =>
          run(() => downloadExport(model.id), "Le fichier n'a pas pu être téléchargé, veuillez réessayer.")
        }
        aria-label={`Télécharger l'export ${model.exportTypeLabel} du ${model.createdAt?.format(DATE_TIME_FORMAT)}`}
      >
        {busy ? <Loader2 className="animate-spin" /> : <Download />}
        Télécharger
      </Button>
    );
  }
  if (model.isFailed) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() =>
          run(async () => {
            await createExport(model.companyId, model.exportTypeReference, model.filters);
            toast.info('Export relancé.');
            await refresh();
          }, "L'export n'a pas pu être relancé, veuillez réessayer.")
        }
      >
        {busy ? <Loader2 className="animate-spin" /> : <RotateCcw />}
        Relancer
      </Button>
    );
  }

  return null;
}
